import { zodResolver } from '@hookform/resolvers/zod'
import { DoorOpen } from 'lucide-react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from '@/components/feedback/toast-store'
import { applyServerErrors } from '@/components/forms/applyServerErrors'
import { FormField } from '@/components/forms/FormField'
import { Button } from '@/components/ui/Button'
import { Dialog } from '@/components/ui/Dialog'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Switch } from '@/components/ui/Switch'
import { humanize } from '@/utils/format'
import { useSaveRoom } from '../api'
import { ROOM_KINDS, roomSchema, type RoomFormValues } from '../schemas'
import type { Room } from '../types'

const FIELDS = ['name', 'kind', 'capacity', 'active']

/** Create / edit a room. Mounted only while open. */
export function RoomDialog({ room, onOpenChange }: { room?: Room; onOpenChange: (open: boolean) => void }) {
  const { t } = useTranslation('timetable')
  const save = useSaveRoom()
  const {
    register,
    control,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<RoomFormValues>({
    resolver: zodResolver(roomSchema),
    defaultValues: {
      name: room?.name ?? '',
      kind: room?.kind ?? 'classroom',
      capacity: room?.capacity ? String(room.capacity) : '',
      active: room?.active ?? true,
    },
  })

  const onSubmit = handleSubmit(async (v) => {
    try {
      await save.mutateAsync({
        id: room?.id,
        name: v.name,
        kind: v.kind,
        capacity: v.capacity ? Number(v.capacity) : null,
        active: v.active,
      })
      toast.success(room ? t('Room updated') : t('Room added'), v.name)
      onOpenChange(false)
    } catch (e) {
      applyServerErrors(e, setError, FIELDS)
    }
  })

  return (
    <Dialog
      open
      onOpenChange={onOpenChange}
      size="sm"
      icon={<DoorOpen />}
      title={room ? t('Edit room') : t('New room')}
      dismissible={!isSubmitting}
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            {t('Cancel')}
          </Button>
          <Button type="submit" form="timetable-room-form" loading={isSubmitting}>
            {t('Save')}
          </Button>
        </>
      }
    >
      <form id="timetable-room-form" onSubmit={onSubmit} noValidate className="space-y-4">
        <FormField label={t('Name')} error={errors.name?.message} required>
          <Input {...register('name')} autoFocus placeholder={t('e.g. Science Lab 1')} maxLength={50} />
        </FormField>
        <div className="grid grid-cols-2 gap-3">
          <FormField label={t('Type')} error={errors.kind?.message} required>
            <Select
              options={ROOM_KINDS.map((k) => ({ value: k, label: humanize(k) }))}
              {...register('kind')}
            />
          </FormField>
          <FormField label={t('Capacity')} error={errors.capacity?.message}>
            <Input type="number" min={1} max={1000} inputMode="numeric" {...register('capacity')} />
          </FormField>
        </div>
        <div className="flex items-start justify-between gap-4 rounded-control border border-line p-3">
          <div>
            <p id="tt-room-active-label" className="text-sm font-medium text-ink">
              {t('Active')}
            </p>
            <p id="tt-room-active-hint" className="text-xs text-muted">
              {t('Inactive rooms keep their lessons but cannot be booked again.')}
            </p>
          </div>
          <Controller
            control={control}
            name="active"
            render={({ field }) => (
              <Switch
                aria-labelledby="tt-room-active-label"
                aria-describedby="tt-room-active-hint"
                checked={field.value}
                onCheckedChange={field.onChange}
              />
            )}
          />
        </div>
      </form>
    </Dialog>
  )
}
